// Config panel for the "json_builder" action block
// Builds a JSON object from key-value pairs or raw JSON, stores as variable
import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
// Detect the JSON type for a value
const detectType = (val) => {
  if (val === null) return 'null';
  if (Array.isArray(val)) return 'array';
  if (typeof val === 'object') return 'object';
  if (typeof val === 'boolean') return 'boolean';
  if (typeof val === 'number') return 'number';
  return 'string';
};

// Flatten a JSON object into field rows using dot notation
// e.g. { data: { id: 123, tags: [1,2] } } → [{ key: "data.id", value: "123", type: "number" }, { key: "data.tags", value: "[1,2]", type: "array" }]
const jsonToFields = (jsonStr) => {
  try {
    const obj = JSON.parse(jsonStr);
    if (typeof obj !== 'object' || obj === null) return null;

    const fields = [];
    const walk = (current, prefix) => {
      for (const [key, val] of Object.entries(current)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const type = detectType(val);

        if (type === 'object' && !Array.isArray(val) && val !== null && Object.keys(val).length > 0) {
          // Recurse into plain objects
          walk(val, path);
        } else {
          // Leaf value — store as a field row
          let displayValue;
          if (val === null) displayValue = '';
          else if (type === 'object' || type === 'array') displayValue = JSON.stringify(val);
          else displayValue = String(val);

          fields.push({ key: path, value: displayValue, type });
        }
      }
    };
    walk(obj, '');
    return fields.length > 0 ? fields : [{ key: '', value: '', type: 'string' }];
  } catch {
    return null;
  }
};

// Build a JSON string from field rows (inverse of jsonToFields)
const fieldsToJson = (fields) => {
  const obj = {};
  for (const f of fields) {
    if (!f.key) continue;
    let val = f.value ?? '';
    // Convert to proper JS type for JSON output
    if (f.type === 'number') {
      const n = Number(val);
      val = isNaN(n) ? val : n;
    } else if (f.type === 'boolean') {
      val = val === 'true' ? true : val === 'false' ? false : val;
    } else if (f.type === 'null') {
      val = null;
    } else if (f.type === 'object' || f.type === 'array') {
      try { val = JSON.parse(val); } catch { /* keep as string */ }
    }

    // Dot notation nesting
    const keys = f.key.split('.');
    let target = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!target[keys[i]] || typeof target[keys[i]] !== 'object') target[keys[i]] = {};
      target = target[keys[i]];
    }
    target[keys[keys.length - 1]] = val;
  }
  return JSON.stringify(obj, null, 2);
};

const FIELD_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'null', label: 'Null' },
  { value: 'object', label: 'Object (JSON)' },
  { value: 'array', label: 'Array (JSON)' },
];

const JsonBuilderConfig = ({ config, onChange, onSummaryChange }) => {
  const mode = config.mode || 'fields';
  const fields = config.fields || [{ key: '', value: '', type: 'string' }];
  const [jsonError, setJsonError] = useState(null);

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    updateSummary(updated);
  };

  const updateSummary = (cfg) => {
    const varName = cfg.outputVariable || 'jsonBody';
    if (cfg.mode === 'raw') {
      onSummaryChange(`→ {{${varName}}} (raw)`);
    } else {
      const count = (cfg.fields || []).filter(f => f.key).length;
      onSummaryChange(`${count} field${count !== 1 ? 's' : ''} → {{${varName}}}`);
    }
  };

  const updateFields = (newFields) => {
    const updated = { ...config, fields: newFields };
    onChange(updated);
    updateSummary(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = fields.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    );
    updateFields(newFields);
  };

  const addField = () => {
    updateFields([...fields, { key: '', value: '', type: 'string' }]);
  };

  const removeField = (index) => {
    if (fields.length <= 1) return;
    updateFields(fields.filter((_, i) => i !== index));
  };

  const handleRawJsonChange = (value) => {
    if (value.trim()) {
      try {
        JSON.parse(value);
        setJsonError(null);
      } catch (e) {
        setJsonError(e.message);
      }
    } else {
      setJsonError(null);
    }
    handleChange('rawJson', value);
  };

  // Live preview of the built JSON
  const preview = useMemo(() => {
    if (mode === 'raw') {
      try {
        return JSON.stringify(JSON.parse(config.rawJson || '{}'), null, 2);
      } catch {
        return config.rawJson || '{}';
      }
    }
    const obj = {};
    for (const f of fields) {
      if (!f.key) continue;
      let val = f.value || '';
      // Show {{var}} as-is in preview
      if (f.type === 'number') val = `<number>`;
      else if (f.type === 'boolean') val = `<boolean>`;
      else if (f.type === 'null') val = null;
      else if (f.type === 'object' || f.type === 'array') {
        try { val = JSON.parse(val); } catch { val = f.value; }
      }

      const keys = f.key.split('.');
      let target = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!target[keys[i]] || typeof target[keys[i]] !== 'object') target[keys[i]] = {};
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = val !== undefined ? (f.value || val) : val;
    }
    return JSON.stringify(obj, null, 2);
  }, [mode, fields, config.rawJson]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Build a JSON object and store it as a variable.
        Use it as the body for an HTTP Request block via <code style={{ fontSize: '0.65rem' }}>{'{{varName.__json}}'}</code>.
      </Typography>

      {/* Output variable name */}
      <TextField
        size="small"
        label="Store As Variable"
        value={config.outputVariable || ''}
        onChange={(e) => handleChange('outputVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        required
        placeholder="e.g. requestBody"
        helperText={
          config.outputVariable
            ? <>Use <code style={{ fontSize: '0.6rem' }}>{`{{${config.outputVariable}.__json}}`}</code> as HTTP body, or <code style={{ fontSize: '0.6rem' }}>{`{{${config.outputVariable}.fieldName}}`}</code> for individual fields</>
            : 'Variable name to store the built JSON object'
        }
      />

      {/* Mode toggle — converts data between modes on switch */}
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, newMode) => {
          if (!newMode || newMode === mode) return;

          if (newMode === 'fields') {
            // Switching to Field Builder → parse raw JSON into fields
            const parsed = jsonToFields(config.rawJson || '{}');
            if (parsed) {
              const updated = { ...config, mode: newMode, fields: parsed };
              onChange(updated);
              updateSummary(updated);
            } else {
              // Couldn't parse (maybe has {{var}} templates) — just switch mode, keep fields as-is
              handleChange('mode', newMode);
            }
          } else {
            // Switching to Raw JSON → build JSON string from fields
            const json = fieldsToJson(fields);
            const updated = { ...config, mode: newMode, rawJson: json };
            onChange(updated);
            updateSummary(updated);
            setJsonError(null);
          }
        }}
        size="small"
        fullWidth
      >
        <ToggleButton value="fields" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          Field Builder
        </ToggleButton>
        <ToggleButton value="raw" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          Raw JSON
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'fields' ? (
        <>
          {fields.map((field, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" fontWeight={600}>Field {index + 1}</Typography>
                {fields.length > 1 && (
                  <IconButton size="small" onClick={() => removeField(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Key"
                  value={field.key || ''}
                  onChange={(e) => handleFieldChange(index, 'key', e.target.value)}
                  placeholder="e.g. locationId"
                  sx={{ flex: 1 }}
                  helperText="Dot notation for nesting: data.id"
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={field.type || 'string'}
                    onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                    label="Type"
                  >
                    {FIELD_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {field.type !== 'null' && (
                <TextField
                  size="small"
                  label="Value"
                  value={field.value ?? ''}
                  onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
                  placeholder={
                    field.type === 'number' ? 'e.g. {{internalNumber}}'
                    : field.type === 'boolean' ? 'true or false or {{varName}}'
                    : field.type === 'object' ? '{"nested": "json"}'
                    : field.type === 'array' ? '[1, 2, 3]'
                    : 'e.g. {{api.results.0.locationName}}'
                  }
                  helperText="Can use {{varName}} to insert variable values"
                  multiline={field.type === 'object' || field.type === 'array'}
                  rows={field.type === 'object' || field.type === 'array' ? 2 : 1}
                  inputProps={{
                    style: (field.type === 'object' || field.type === 'array')
                      ? { fontFamily: 'monospace', fontSize: '0.75rem' }
                      : {}
                  }}
                />
              )}
            </Paper>
          ))}

          <Button size="small" startIcon={<AddIcon />} onClick={addField} variant="outlined">
            Add Field
          </Button>
        </>
      ) : (
        <TextField
          size="small"
          label="Raw JSON"
          value={config.rawJson || ''}
          onChange={(e) => handleRawJsonChange(e.target.value)}
          multiline
          rows={8}
          error={!!jsonError}
          helperText={jsonError || 'Write raw JSON. Use {{varName}} to inject variable values.'}
          placeholder={'{\n  "action": "open_door",\n  "locationId": {{api.results.0.locationId}},\n  "extension": "{{internalNumber}}"\n}'}
          InputProps={{
            sx: { fontFamily: 'monospace', fontSize: '0.75rem' }
          }}
        />
      )}

      {/* Preview */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Preview
        </Typography>
        <Box
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.65rem',
            m: 0,
            p: 1,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 150,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {preview}
        </Box>
        {config.outputVariable && (
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={`{{${config.outputVariable}.__json}}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.6rem', fontFamily: 'monospace' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              ← use this as HTTP Request body
            </Typography>
          </Box>
        )}
      </Paper>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Workflow:</strong><br />
          1. Add fields with keys and values (use <code style={{ fontSize: '0.6rem' }}>{'{{varName}}'}</code> for dynamic values)<br />
          2. Set "Store As Variable" name (e.g. <code style={{ fontSize: '0.6rem' }}>requestBody</code>)<br />
          3. In the HTTP Request block, set body to: <code style={{ fontSize: '0.6rem' }}>{'{{requestBody.__json}}'}</code><br />
          4. Individual fields also available: <code style={{ fontSize: '0.6rem' }}>{'{{requestBody.locationId}}'}</code>
        </Typography>
      </Alert>
    </Box>
  );
};

export default JsonBuilderConfig;
